type ApiOk<T> = { success: true; data: T }
type ApiErr = { success: false; error: string; message?: string; data?: unknown }

/** Erro da API com o `data` do corpo (ex.: lista do que falta) — é um Error comum para quem só lê a mensagem. */
export class ApiError extends Error {
  status: number
  data: unknown
  constructor(message: string, status: number, data: unknown) {
    super(message)
    this.status = status
    this.data = data
  }
}

export type ApiResponse<T> = ApiOk<T> | ApiErr

let csrfToken: string | null = null

async function parseJsonSafe(res: Response) {
  try {
    return (await res.json()) as unknown
  } catch {
    return null
  }
}

export async function getCsrfToken() {
  if (csrfToken) return csrfToken
  const res = await fetch('/api/auth/csrf', { credentials: 'include' })
  const json = (await parseJsonSafe(res)) as ApiResponse<{ csrfToken: string }> | null
  if (!res.ok || !json || json.success !== true) {
    throw new Error('Falha ao obter CSRF')
  }
  csrfToken = json.data.csrfToken
  return csrfToken
}

export function clearCsrfToken() {
  csrfToken = null
}

export async function apiFetch<T>(
  path: string,
  opts?: Omit<RequestInit, 'headers'> & { headers?: Record<string, string>; csrf?: boolean },
): Promise<T> {
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    ...(opts?.headers ?? {}),
  }

  if (opts?.csrf) {
    headers['x-csrf-token'] = await getCsrfToken()
  }

  const init: RequestInit = {
    ...opts,
    headers,
    credentials: 'include',
  }

  if (init.cache == null) {
    init.cache = 'no-store'
  }

  const res = await fetch(path, init)

  const json = (await parseJsonSafe(res)) as ApiResponse<T> | null

  if (res.status === 403 && json && json.success === false && json.error === 'CSRF inválido' && opts?.csrf) {
    clearCsrfToken()
    return apiFetch(path, opts)
  }

  if (!json) {
    throw new Error(`Falha na requisição (HTTP ${res.status})`)
  }
  if (json.success !== true) {
    throw new ApiError(json.message ? `${json.error}: ${json.message}` : json.error, res.status, json.data)
  }
  if (!res.ok) {
    throw new Error('Falha na requisição')
  }
  return json.data
}

/** Erro que não prova ausência de sessão: rate limit, 5xx, rede/JSON inválido. */
export function isTransientAuthError(e: unknown): boolean {
  if (e instanceof ApiError) return e.status === 429 || e.status >= 500
  // apiFetch lança Error comum quando não há JSON (proxy caiu, HTTP 502 cru) e fetch lança TypeError na rede.
  return e instanceof TypeError || (e instanceof Error && /HTTP (429|5\d\d)/.test(e.message))
}
